import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { 
  WorkflowModel, WorkflowStateModel, WorkflowTransitionModel, WorkflowConditionModel,
  TreeNode, LayoutTransition, DropZone, WorkflowLayout, Selection,
  CONDITION_OPERATORS, COMMON_CONDITION_PROPERTIES, ConditionPropertyDefinition
} from './workflow.models';

@Component({
    selector: 'app-workflow-editor',
    templateUrl: './workflow-editor.component.html',
    styleUrls: ['./workflow-editor.component.scss'],
    standalone: false
})
export class WorkflowEditorComponent implements OnChanges, AfterViewInit {
  @Input() workflow: WorkflowModel | null = null;
  @Input() readonly = false;
  @Input() availableProperties: ConditionPropertyDefinition[] = [];
  @Output() workflowChange = new EventEmitter<WorkflowModel>();

  @ViewChild('canvasContainer') canvasContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('svgElement') svgElement!: ElementRef<SVGSVGElement>;

  workflowCopy: WorkflowModel | null = null;
  layout: WorkflowLayout | null = null;
  selection: Selection = { type: null };

  // Layout constants
  readonly STATE_WIDTH = 180;
  readonly STATE_HEIGHT = 64;
  readonly ROW_GAP = 100;
  readonly MIN_COL_GAP = 40;
  readonly CANVAS_PADDING = 60;
  readonly MIN_WIDTH = 900;

  // Drag state
  isDragging = false;
  draggedNode: TreeNode | null = null;
  dragOffset = { x: 0, y: 0 };
  dragPosition = { x: 0, y: 0 };
  activeDropZone: DropZone | null = null;

  // Connection dragging
  isConnecting = false;
  connectionSource: TreeNode | null = null;
  connectionLine = { x1: 0, y1: 0, x2: 0, y2: 0 };

  // View state
  viewBox = { x: 0, y: 0, width: 900, height: 600 };
  isPanning = false;
  panStart = { x: 0, y: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflow']) {
      const workflowChange = changes['workflow'];
      const previousId = workflowChange.previousValue?.Id;
      const currentId = workflowChange.currentValue?.Id;
      
      // Only clear selection on initial load or when switching to a different workflow
      // Don't clear when the same workflow is updated (e.g., after emitChange)
      const isNewWorkflow = workflowChange.firstChange || previousId !== currentId;
      
      if (isNewWorkflow) {
        this.workflowCopy = this.workflow ? this.cloneWorkflow(this.workflow) : null;
        this.selection = { type: null };
        this.computeLayout();
      }
      // If it's an update to the same workflow, don't reset the copy or selection
      // The internal workflowCopy already has the changes
    }
  }

  ngAfterViewInit(): void {
    this.computeLayout();
  }

  get hasWorkflow(): boolean {
    return !!this.workflowCopy;
  }

  get selectedState(): WorkflowStateModel | null {
    return this.selection.type === 'state' ? this.selection.state ?? null : null;
  }

  get selectedTransition(): WorkflowTransitionModel | null {
    return this.selection.type === 'transition' ? this.selection.transition ?? null : null;
  }

  // === Tree-Based Layout ===

  computeLayout(): void {
    if (!this.workflowCopy) {
      this.layout = null;
      return;
    }

    const states = this.workflowCopy.States;
    const transitions = this.workflowCopy.Transitions;

    if (states.length === 0) {
      // Empty workflow - just show drop zone for first state
      this.layout = {
        nodes: [],
        transitions: [],
        dropZones: [{
          id: 'dz-initial',
          x: this.CANVAS_PADDING,
          y: this.CANVAS_PADDING + 60,
          width: this.MIN_WIDTH - this.CANVAS_PADDING * 2,
          height: 80,
          parentStateKey: null,
          insertIndex: 0,
          label: 'Drop or click to add initial state'
        }],
        width: this.MIN_WIDTH,
        height: 300
      };
      this.viewBox = { x: 0, y: 0, width: this.MIN_WIDTH, height: 300 };
      return;
    }

    // Build tree from transitions
    const tree = this.buildTree(states, transitions);
    
    // Calculate layout bounds for tree
    const totalWidth = Math.max(this.MIN_WIDTH, this.calculateTreeWidth(tree));
    
    // Position nodes
    this.positionTree(tree, this.CANVAS_PADDING, totalWidth - this.CANVAS_PADDING);
    
    // Flatten tree to get all nodes
    const nodes = this.flattenTree(tree);
    
    // Count sibling transitions per depth level (for drop zone positioning)
    const siblingTransitionsPerDepth = this.countSiblingTransitionsPerDepth(transitions, nodes);
    
    // Calculate transitions
    const layoutTransitions = this.calculateTransitions(transitions, nodes);
    
    // Calculate drop zones (positioned below any sibling transition routing)
    const dropZones = this.calculateDropZones(nodes, siblingTransitionsPerDepth);
    
    // Calculate total height - account for sibling transitions
    const maxDepth = Math.max(...nodes.map(n => n.depth), 0);
    const maxSiblingTransitions = Math.max(...Array.from(siblingTransitionsPerDepth.values()), 0);
    const extraHeight = maxSiblingTransitions * 25;
    const height = this.CANVAS_PADDING * 2 + 60 + (maxDepth + 1) * (this.STATE_HEIGHT + this.ROW_GAP) + extraHeight + 150;

    this.layout = { nodes, transitions: layoutTransitions, dropZones, width: totalWidth, height };
    this.viewBox = { x: 0, y: 0, width: totalWidth, height };
  }

  private buildTree(states: WorkflowStateModel[], transitions: WorkflowTransitionModel[]): TreeNode[] {
    // For each state, find its PRIMARY parent based on transition priority
    // The transition with the LOWEST priority number is the primary parent for layout
    const primaryParent = new Map<string, string | null>();
    
    for (const state of states) {
      // Get all incoming transitions to this state, sorted by priority (lowest first)
      const incomingTransitions = transitions
        .filter(t => t.ToStateKey === state.StateKey)
        .sort((a, b) => (a.Priority ?? 999) - (b.Priority ?? 999));
      
      if (incomingTransitions.length > 0) {
        primaryParent.set(state.StateKey, incomingTransitions[0].FromStateKey);
      } else {
        primaryParent.set(state.StateKey, null);
      }
    }

    // Build children map based on primary parent relationships
    const children = new Map<string, string[]>();
    for (const state of states) {
      children.set(state.StateKey, []);
    }
    
    for (const state of states) {
      const parent = primaryParent.get(state.StateKey);
      if (parent && children.has(parent)) {
        children.get(parent)!.push(state.StateKey);
      }
    }

    // Sort children by their transition priority for consistent ordering
    for (const [parentKey, childKeys] of children.entries()) {
      childKeys.sort((a, b) => {
        const transA = transitions.find(t => t.FromStateKey === parentKey && t.ToStateKey === a);
        const transB = transitions.find(t => t.FromStateKey === parentKey && t.ToStateKey === b);
        return (transA?.Priority ?? 999) - (transB?.Priority ?? 999);
      });
    }

    // Find root states (initial or no primary parent)
    let rootStates = states.filter(s => s.IsInitial);
    if (rootStates.length === 0) {
      rootStates = states.filter(s => primaryParent.get(s.StateKey) === null);
    }
    if (rootStates.length === 0 && states.length > 0) {
      rootStates = [states[0]];
    }

    const nodeMap = new Map<string, TreeNode>();

    const buildNode = (state: WorkflowStateModel, parent: TreeNode | null, depth: number): TreeNode => {
      const node: TreeNode = {
        state,
        children: [],
        parent,
        depth,
        leftBound: 0,
        rightBound: 0,
        x: 0,
        y: 0,
        width: this.STATE_WIDTH,
        height: this.STATE_HEIGHT
      };
      nodeMap.set(state.StateKey, node);
      return node;
    };

    const roots: TreeNode[] = [];
    const visited = new Set<string>();

    // Build tree recursively based on primary parent relationships
    const buildSubtree = (state: WorkflowStateModel, parent: TreeNode | null, depth: number): TreeNode | null => {
      if (visited.has(state.StateKey)) return null;
      visited.add(state.StateKey);
      
      const node = buildNode(state, parent, depth);
      
      const childKeys = children.get(state.StateKey) || [];
      for (const childKey of childKeys) {
        const childState = states.find(s => s.StateKey === childKey);
        if (childState) {
          const childNode = buildSubtree(childState, node, depth + 1);
          if (childNode) {
            node.children.push(childNode);
          }
        }
      }
      
      return node;
    };

    // Process roots
    for (const rootState of rootStates) {
      const rootNode = buildSubtree(rootState, null, 0);
      if (rootNode) {
        roots.push(rootNode);
      }
    }

    // Handle any disconnected states (states not reachable from roots)
    for (const state of states) {
      if (!visited.has(state.StateKey)) {
        const node = buildSubtree(state, null, 0);
        if (node) {
          roots.push(node);
        }
      }
    }

    return roots;
  }

  private calculateTreeWidth(roots: TreeNode[]): number {
    const countLeaves = (node: TreeNode): number => {
      if (node.children.length === 0) return 1;
      return node.children.reduce((sum, child) => sum + countLeaves(child), 0);
    };

    let totalLeaves = 0;
    for (const root of roots) {
      totalLeaves += countLeaves(root);
    }

    return this.CANVAS_PADDING * 2 + totalLeaves * (this.STATE_WIDTH + this.MIN_COL_GAP);
  }

  private positionTree(roots: TreeNode[], left: number, right: number): void {
    const totalRoots = roots.length;
    const widthPerRoot = (right - left) / totalRoots;

    for (let i = 0; i < roots.length; i++) {
      const rootLeft = left + i * widthPerRoot;
      const rootRight = rootLeft + widthPerRoot;
      this.positionNode(roots[i], rootLeft, rootRight);
    }
  }

  private positionNode(node: TreeNode, left: number, right: number): void {
    node.leftBound = left;
    node.rightBound = right;
    
    // Center the node in its bounds
    const centerX = (left + right) / 2;
    node.x = centerX - node.width / 2;
    node.y = this.CANVAS_PADDING + 60 + node.depth * (this.STATE_HEIGHT + this.ROW_GAP);

    // Position children
    if (node.children.length > 0) {
      const widthPerChild = (right - left) / node.children.length;
      
      for (let i = 0; i < node.children.length; i++) {
        const childLeft = left + i * widthPerChild;
        const childRight = childLeft + widthPerChild;
        this.positionNode(node.children[i], childLeft, childRight);
      }
    }
  }

  private flattenTree(roots: TreeNode[]): TreeNode[] {
    const result: TreeNode[] = [];
    
    const traverse = (node: TreeNode) => {
      result.push(node);
      for (const child of node.children) {
        traverse(child);
      }
    };

    for (const root of roots) {
      traverse(root);
    }

    return result;
  }

  private calculateTransitions(transitions: WorkflowTransitionModel[], nodes: TreeNode[]): LayoutTransition[] {
    const nodeMap = new Map<string, TreeNode>();
    for (const node of nodes) {
      nodeMap.set(node.state.StateKey, node);
    }

    // Group transitions by routing type
    const siblingTransitions: { t: WorkflowTransitionModel; from: TreeNode; to: TreeNode }[] = [];
    const backwardTransitions: { t: WorkflowTransitionModel; from: TreeNode; to: TreeNode }[] = [];
    const otherTransitions: { t: WorkflowTransitionModel; from: TreeNode; to: TreeNode }[] = [];

    for (const t of transitions) {
      const fromNode = nodeMap.get(t.FromStateKey);
      const toNode = nodeMap.get(t.ToStateKey);
      if (!fromNode || !toNode) continue;

      const isBackward = toNode.depth < fromNode.depth;
      const isSameLevel = fromNode.depth === toNode.depth;

      if (isSameLevel) {
        siblingTransitions.push({ t, from: fromNode, to: toNode });
      } else if (isBackward) {
        backwardTransitions.push({ t, from: fromNode, to: toNode });
      } else {
        otherTransitions.push({ t, from: fromNode, to: toNode });
      }
    }

    // For sibling transitions, calculate exit/entry offsets per state
    // Count TOTAL connections (both in and out) to spread them all along the bottom edge
    const totalConnectionsPerState = new Map<string, number>();
    const connectionIndexPerState = new Map<string, number>();
    
    // Count all sibling connections per state (both as source and target)
    for (const { from, to } of siblingTransitions) {
      totalConnectionsPerState.set(from.state.StateKey, (totalConnectionsPerState.get(from.state.StateKey) || 0) + 1);
      totalConnectionsPerState.set(to.state.StateKey, (totalConnectionsPerState.get(to.state.StateKey) || 0) + 1);
    }

    const layoutTransitions: LayoutTransition[] = [];

    // Process sibling transitions with spread connection points
    siblingTransitions.forEach(({ t, from, to }, routeIndex) => {
      // Get connection offset for source state (all connections share the bottom edge)
      const exitTotal = totalConnectionsPerState.get(from.state.StateKey) || 1;
      const exitIndex = connectionIndexPerState.get(from.state.StateKey) || 0;
      connectionIndexPerState.set(from.state.StateKey, exitIndex + 1);
      
      // Get connection offset for target state
      const entryTotal = totalConnectionsPerState.get(to.state.StateKey) || 1;
      const entryIndex = connectionIndexPerState.get(to.state.StateKey) || 0;
      connectionIndexPerState.set(to.state.StateKey, entryIndex + 1);
      
      const { path, labelX, labelY } = this.calculateTransitionPath(
        from, to, false, routeIndex,
        { exitIndex, exitTotal, entryIndex, entryTotal }
      );
      layoutTransitions.push({
        transition: t,
        fromNode: from,
        toNode: to,
        path,
        labelX,
        labelY,
        isBackward: false
      });
    });

    // Process backward transitions with spacing indices
    backwardTransitions.forEach(({ t, from, to }, index) => {
      const { path, labelX, labelY } = this.calculateTransitionPath(from, to, true, index);
      layoutTransitions.push({
        transition: t,
        fromNode: from,
        toNode: to,
        path,
        labelX,
        labelY,
        isBackward: true
      });
    });

    // Process other transitions (forward)
    for (const { t, from, to } of otherTransitions) {
      const { path, labelX, labelY } = this.calculateTransitionPath(from, to, false, 0);
      layoutTransitions.push({
        transition: t,
        fromNode: from,
        toNode: to,
        path,
        labelX,
        labelY,
        isBackward: false
      });
    }

    return layoutTransitions;
  }

  private calculateTransitionPath(
    from: TreeNode, 
    to: TreeNode, 
    isBackward: boolean, 
    siblingIndex: number = 0,
    connectionOffsets?: { exitIndex: number; exitTotal: number; entryIndex: number; entryTotal: number }
  ): { path: string; labelX: number; labelY: number } {
    const fromCenterX = from.x + from.width / 2;
    const fromBottom = from.y + from.height;
    const fromTop = from.y;
    const toCenterX = to.x + to.width / 2;
    const toTop = to.y;
    const toBottom = to.y + to.height;

    let path: string;
    let labelX: number;
    let labelY: number;

    const r = 10; // Corner radius

    if (isBackward) {
      // Loop back to earlier state - find the shortest path
      // Check if target is mostly above us (within horizontal bounds)
      const fromLeft = from.x;
      const fromRight = from.x + from.width;
      const toLeft = to.x;
      const toRight = to.x + to.width;
      
      // Check for horizontal overlap - if overlapping, go straight up
      const hasHorizontalOverlap = !(fromRight < toLeft || fromLeft > toRight);
      
      // Also check if going straight up would be shorter than routing around
      const horizontalDistance = Math.abs(fromCenterX - toCenterX);
      const goStraightUp = hasHorizontalOverlap || horizontalDistance < from.width;
      
      if (goStraightUp) {
        // Route straight up - exit from top, curve to target's bottom
        const midY = (fromTop + toBottom) / 2;
        
        path = `M ${fromCenterX} ${fromTop}
                C ${fromCenterX} ${midY},
                  ${toCenterX} ${midY},
                  ${toCenterX} ${toBottom}`;
        
        labelX = (fromCenterX + toCenterX) / 2 + (fromCenterX < toCenterX ? -60 : 60);
        labelY = midY;
      } else {
        // Route to the side - determine which side is shorter
        const distanceToLeft = fromLeft - toLeft;
        const distanceToRight = toRight - fromRight;
        const goRight = distanceToRight < distanceToLeft;
        
        const offset = 50 + siblingIndex * 20;
        const routeX = goRight 
          ? Math.max(fromRight, toRight) + offset
          : Math.min(fromLeft, toLeft) - offset;
        
        const fromY = from.y + from.height / 2;
        const toY = to.y + to.height / 2;
        
        if (goRight) {
          path = `M ${fromRight} ${fromY}
                  L ${routeX - r} ${fromY}
                  A ${r} ${r} 0 0 0 ${routeX} ${fromY - r}
                  L ${routeX} ${toY + r}
                  A ${r} ${r} 0 0 0 ${routeX - r} ${toY}
                  L ${toRight} ${toY}`;
        } else {
          path = `M ${fromLeft} ${fromY}
                  L ${routeX + r} ${fromY}
                  A ${r} ${r} 0 0 1 ${routeX} ${fromY - r}
                  L ${routeX} ${toY + r}
                  A ${r} ${r} 0 0 1 ${routeX + r} ${toY}
                  L ${toLeft} ${toY}`;
        }
        
        labelX = routeX + (goRight ? 15 : -15);
        labelY = (fromY + toY) / 2;
      }
      
    } else if (from.depth === to.depth) {
      // Same level (siblings) - clean U-shaped path below states
      // Spread out connection points if multiple transitions share the same state
      const routeY = Math.max(fromBottom, toBottom) + 25 + siblingIndex * 22;
      
      // Calculate exit point offset (spread across bottom of source state)
      let startX = fromCenterX;
      if (connectionOffsets && connectionOffsets.exitTotal > 1) {
        const spreadWidth = from.width * 0.8; // Use 80% of width for spreading
        const step = spreadWidth / (connectionOffsets.exitTotal + 1);
        startX = from.x + (from.width - spreadWidth) / 2 + step * (connectionOffsets.exitIndex + 1);
      }
      
      // Calculate entry point offset (spread across bottom of target state)
      let endX = toCenterX;
      if (connectionOffsets && connectionOffsets.entryTotal > 1) {
        const spreadWidth = to.width * 0.8; // Use 80% of width for spreading
        const step = spreadWidth / (connectionOffsets.entryTotal + 1);
        endX = to.x + (to.width - spreadWidth) / 2 + step * (connectionOffsets.entryIndex + 1);
      }
      
      // Debug: log entry info
      // console.log(`Sibling to ${to.state.StateKey}: entryIndex=${connectionOffsets?.entryIndex}, entryTotal=${connectionOffsets?.entryTotal}, endX=${endX}, toCenterX=${toCenterX}`);
      
      const goingRight = endX > startX;
      
      // Simple U-shape: down, across, up - go straight up to entry point
      // Use arc commands for clean rounded corners
      if (goingRight) {
        path = `M ${startX} ${fromBottom}
                L ${startX} ${routeY - r}
                A ${r} ${r} 0 0 0 ${startX + r} ${routeY}
                L ${endX - r} ${routeY}
                A ${r} ${r} 0 0 0 ${endX} ${routeY - r}
                L ${endX} ${toBottom}`;
      } else {
        path = `M ${startX} ${fromBottom}
                L ${startX} ${routeY - r}
                A ${r} ${r} 0 0 1 ${startX - r} ${routeY}
                L ${endX + r} ${routeY}
                A ${r} ${r} 0 0 1 ${endX} ${routeY - r}
                L ${endX} ${toBottom}`;
      }
      
      labelX = (startX + endX) / 2;
      labelY = routeY + 15;
      
    } else if (to.depth > from.depth) {
      // Forward - going down to a child/descendant
      const midY = (fromBottom + toTop) / 2;
      
      // Smooth bezier curve
      path = `M ${fromCenterX} ${fromBottom}
              C ${fromCenterX} ${midY},
                ${toCenterX} ${midY},
                ${toCenterX} ${toTop}`;
      
      // Position label to the side to avoid overlap with arrow
      const labelOffset = Math.abs(toCenterX - fromCenterX) < 50 ? 60 : 50;
      labelX = (fromCenterX + toCenterX) / 2 + (toCenterX > fromCenterX ? labelOffset : -labelOffset);
      labelY = midY;
      
    } else {
      // Going up to an ancestor - route to the side
      const offset = 60 + siblingIndex * 25;
      const routeX = fromCenterX > toCenterX 
        ? Math.min(from.x, to.x) - offset 
        : Math.max(from.x + from.width, to.x + to.width) + offset;
      const goingRight = routeX > fromCenterX;
      
      // Path: up from source, across, down to target
      if (goingRight) {
        path = `M ${fromCenterX} ${fromTop}
                L ${fromCenterX} ${fromTop - 20 - r}
                A ${r} ${r} 0 0 1 ${fromCenterX + r} ${fromTop - 20 - r * 2}
                L ${routeX - r} ${fromTop - 20 - r * 2}
                A ${r} ${r} 0 0 1 ${routeX} ${fromTop - 20 - r}
                L ${routeX} ${toBottom + 20 + r}
                A ${r} ${r} 0 0 1 ${routeX - r} ${toBottom + 20 + r * 2}
                L ${toCenterX + r} ${toBottom + 20 + r * 2}
                A ${r} ${r} 0 0 1 ${toCenterX} ${toBottom + 20 + r}
                L ${toCenterX} ${toBottom}`;
      } else {
        path = `M ${fromCenterX} ${fromTop}
                L ${fromCenterX} ${fromTop - 20 - r}
                A ${r} ${r} 0 0 0 ${fromCenterX - r} ${fromTop - 20 - r * 2}
                L ${routeX + r} ${fromTop - 20 - r * 2}
                A ${r} ${r} 0 0 0 ${routeX} ${fromTop - 20 - r}
                L ${routeX} ${toBottom + 20 + r}
                A ${r} ${r} 0 0 0 ${routeX + r} ${toBottom + 20 + r * 2}
                L ${toCenterX - r} ${toBottom + 20 + r * 2}
                A ${r} ${r} 0 0 0 ${toCenterX} ${toBottom + 20 + r}
                L ${toCenterX} ${toBottom}`;
      }
      
      labelX = routeX + (goingRight ? 20 : -20);
      labelY = (fromTop + toBottom) / 2;
    }

    return { path, labelX, labelY };
  }

  private countSiblingTransitionsPerDepth(transitions: WorkflowTransitionModel[], nodes: TreeNode[]): Map<number, number> {
    const nodeMap = new Map<string, TreeNode>();
    for (const node of nodes) {
      nodeMap.set(node.state.StateKey, node);
    }
    
    const countPerDepth = new Map<number, number>();
    
    for (const t of transitions) {
      const fromNode = nodeMap.get(t.FromStateKey);
      const toNode = nodeMap.get(t.ToStateKey);
      if (!fromNode || !toNode) continue;
      
      // Count sibling transitions (same depth)
      if (fromNode.depth === toNode.depth) {
        const depth = fromNode.depth;
        countPerDepth.set(depth, (countPerDepth.get(depth) || 0) + 1);
      }
    }
    
    return countPerDepth;
  }

  private calculateDropZones(nodes: TreeNode[], siblingTransitionsPerDepth: Map<number, number>): DropZone[] {
    const zones: DropZone[] = [];
    let id = 0;

    // Create drop zone for EVERY state (we'll handle terminal states in UI)
    for (const node of nodes) {
      // Calculate zone width - make it wider to be easier to hit
      const zoneWidth = Math.max(node.width, node.rightBound - node.leftBound - 20);
      const zoneCenterX = (node.leftBound + node.rightBound) / 2;
      
      // Calculate Y position - must be below any sibling transition routing at this depth
      const siblingTransitionCount = siblingTransitionsPerDepth.get(node.depth) || 0;
      const transitionRoutingSpace = siblingTransitionCount > 0 
        ? 25 + siblingTransitionCount * 22 + 15  // Base offset + (count * spacing) + padding
        : 10;
      
      zones.push({
        id: `dz-${id++}`,
        x: zoneCenterX - zoneWidth / 2,
        y: node.y + node.height + transitionRoutingSpace,
        width: zoneWidth,
        height: 45,
        parentStateKey: node.state.StateKey,
        insertIndex: node.children.length,
        label: node.state.IsFinal ? 'Terminal state' : 'Add child state',
        isDisabled: node.state.IsFinal
      });
    }

    return zones;
  }

  // === Selection ===

  selectState(state: WorkflowStateModel, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isDragging && !this.isConnecting) {
      this.selection = { type: 'state', state };
    }
  }

  selectTransition(transition: WorkflowTransitionModel, event: MouseEvent): void {
    event.stopPropagation();
    this.selection = { type: 'transition', transition };
  }

  clearSelection(): void {
    if (!this.isDragging && !this.isConnecting) {
      this.selection = { type: null };
    }
  }

  isStateSelected(state: WorkflowStateModel): boolean {
    return this.selection.type === 'state' && this.selection.state?.StateKey === state.StateKey;
  }

  isTransitionSelected(transition: WorkflowTransitionModel): boolean {
    return this.selection.type === 'transition' && this.selection.transition === transition;
  }

  getStateClass(state: WorkflowStateModel): string {
    const classes = ['state-node'];
    if (state.IsInitial) classes.push('initial');
    if (state.IsFinal) classes.push('final');
    if (!state.IsInitial && !state.IsFinal) classes.push('active');
    if (this.isStateSelected(state)) classes.push('selected');
    return classes.join(' ');
  }

  // === Drag & Drop ===

  startDrag(node: TreeNode, event: MouseEvent): void {
    if (this.readonly) return;
    event.stopPropagation();
    event.preventDefault();

    this.isDragging = true;
    this.draggedNode = node;
    
    const point = this.getSvgPoint(event);
    this.dragOffset = { x: point.x - node.x, y: point.y - node.y };
    this.dragPosition = { x: node.x, y: node.y };
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging && this.draggedNode) {
      const point = this.getSvgPoint(event);
      this.dragPosition = { 
        x: point.x - this.dragOffset.x, 
        y: point.y - this.dragOffset.y 
      };
      this.updateActiveDropZone(point);
    }

    if (this.isConnecting && this.connectionSource) {
      const point = this.getSvgPoint(event);
      this.connectionLine.x2 = point.x;
      this.connectionLine.y2 = point.y;
    }

    if (this.isPanning) {
      const dx = event.clientX - this.panStart.x;
      const dy = event.clientY - this.panStart.y;
      const scale = this.viewBox.width / (this.canvasContainer?.nativeElement.clientWidth || 1);
      this.viewBox.x -= dx * scale;
      this.viewBox.y -= dy * scale;
      this.panStart = { x: event.clientX, y: event.clientY };
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isDragging && this.draggedNode) {
      this.finishDrag();
    }
    if (this.isConnecting) {
      this.finishConnection(event);
    }
    this.isPanning = false;
  }

  private updateActiveDropZone(point: { x: number; y: number }): void {
    if (!this.layout) return;

    this.activeDropZone = null;
    
    for (const zone of this.layout.dropZones) {
      if (point.x >= zone.x && point.x <= zone.x + zone.width &&
          point.y >= zone.y && point.y <= zone.y + zone.height) {
        // Don't allow dropping on:
        // 1. Self's drop zone (can't be your own child)
        // 2. Disabled zones (terminal states)
        if (zone.parentStateKey !== this.draggedNode?.state.StateKey && !zone.isDisabled) {
          this.activeDropZone = zone;
        }
        break;
      }
    }
  }

  private finishDrag(): void {
    if (this.activeDropZone && this.draggedNode && this.workflowCopy) {
      this.moveStateToNewParent(this.draggedNode.state, this.activeDropZone);
    }
    
    this.isDragging = false;
    this.draggedNode = null;
    this.activeDropZone = null;
  }

  private moveStateToNewParent(state: WorkflowStateModel, dropZone: DropZone): void {
    if (!this.workflowCopy) return;

    const newParentKey = dropZone.parentStateKey;
    
    // Don't move to self
    if (newParentKey === state.StateKey) return;

    // Check if this would create a cycle
    if (newParentKey && this.wouldCreateCycle(state.StateKey, newParentKey)) {
      console.warn('Cannot move state: would create a cycle in the workflow');
      return;
    }

    // Get current incoming transitions to this state
    const currentIncomingTransitions = this.workflowCopy.Transitions.filter(t => t.ToStateKey === state.StateKey);
    
    // Check if the new parent already has a transition to this state
    const existingTransitionFromNewParent = currentIncomingTransitions.find(t => t.FromStateKey === newParentKey);
    
    if (existingTransitionFromNewParent) {
      // The transition exists - just make it the PRIMARY parent by setting lowest priority
      // First, increase priority of all other incoming transitions
      for (const t of currentIncomingTransitions) {
        if (t !== existingTransitionFromNewParent) {
          t.Priority = (t.Priority ?? 1) + 1;
        }
      }
      // Set the new parent's transition as primary (lowest priority = first)
      existingTransitionFromNewParent.Priority = 0;
    } else if (newParentKey !== null) {
      // No existing transition from new parent - remove old transitions and create new one
      // Remove all incoming transitions (we're moving to a new parent)
      this.workflowCopy.Transitions = this.workflowCopy.Transitions.filter(t => t.ToStateKey !== state.StateKey);
      
      state.IsInitial = false;
      
      // Add transition from new parent with priority 0 (primary)
      const newTransition: WorkflowTransitionModel = {
        FromStateKey: newParentKey,
        ToStateKey: state.StateKey,
        Name: `${newParentKey} → ${state.StateKey}`,
        Description: '',
        IsManualTrigger: true,
        IsAutomaticTrigger: false,
        MinimumTimeInSourceState: 0,
        Priority: 0
      };
      this.workflowCopy.Transitions.push(newTransition);
    } else {
      // Moving to root - remove incoming transitions and make it initial
      this.workflowCopy.Transitions = this.workflowCopy.Transitions.filter(t => t.ToStateKey !== state.StateKey);
      state.IsInitial = true;
    }

    this.emitChange();
    this.computeLayout();
  }

  /**
   * Check if adding a transition from newParent to state would create a cycle.
   * This happens if state has any outgoing path that reaches newParent.
   */
  private wouldCreateCycle(stateKey: string, newParentKey: string): boolean {
    if (!this.workflowCopy) return false;
    
    // BFS from stateKey following OUTGOING transitions
    // If we can reach newParentKey, then adding newParentKey → stateKey would create a cycle
    const visited = new Set<string>();
    const queue = [stateKey];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);
      
      // Get all states reachable from current via outgoing transitions
      const reachable = this.workflowCopy.Transitions
        .filter(t => t.FromStateKey === current)
        .map(t => t.ToStateKey);
      
      if (reachable.includes(newParentKey)) {
        return true; // Found a path from state to newParent - would create cycle
      }
      
      queue.push(...reachable);
    }
    
    return false;
  }

  isDropZoneActive(zone: DropZone): boolean {
    return this.activeDropZone?.id === zone.id;
  }

  // === Connection Dragging ===

  startConnection(node: TreeNode, event: MouseEvent): void {
    if (this.readonly) return;
    event.stopPropagation();
    event.preventDefault();

    this.isConnecting = true;
    this.connectionSource = node;
    
    this.connectionLine = {
      x1: node.x + node.width / 2,
      y1: node.y + node.height,
      x2: node.x + node.width / 2,
      y2: node.y + node.height
    };
  }

  private finishConnection(event: MouseEvent): void {
    if (!this.connectionSource || !this.layout || !this.workflowCopy) {
      this.isConnecting = false;
      this.connectionSource = null;
      return;
    }

    const point = this.getSvgPoint(event);

    // Find target state
    const targetNode = this.layout.nodes.find(n =>
      point.x >= n.x && point.x <= n.x + n.width &&
      point.y >= n.y && point.y <= n.y + n.height &&
      n.state.StateKey !== this.connectionSource!.state.StateKey
    );

    if (targetNode) {
      // Check if transition already exists
      const exists = this.workflowCopy.Transitions.some(
        t => t.FromStateKey === this.connectionSource!.state.StateKey && 
             t.ToStateKey === targetNode.state.StateKey
      );

      if (!exists) {
        const transition: WorkflowTransitionModel = {
          Id: null,
          FromStateKey: this.connectionSource.state.StateKey,
          ToStateKey: targetNode.state.StateKey,
          Name: 'New Transition',
          Description: '',
          IsManualTrigger: true,
          IsAutomaticTrigger: false,
          MinimumTimeInSourceState: 0,
          Priority: 1
        };

        this.workflowCopy.Transitions.push(transition);
        this.selection = { type: 'transition', transition };
        this.computeLayout();
        this.emitChange();
      }
    }

    this.isConnecting = false;
    this.connectionSource = null;
  }

  isConnectionTarget(node: TreeNode): boolean {
    if (!this.isConnecting || !this.connectionSource) return false;
    if (node.state.StateKey === this.connectionSource.state.StateKey) return false;

    const dx = this.connectionLine.x2 - (node.x + node.width / 2);
    const dy = this.connectionLine.y2 - (node.y + node.height / 2);
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < 70;
  }

  // === State Management ===

  addState(parentStateKey?: string | null): void {
    if (this.readonly || !this.workflowCopy) return;

    const newKey = this.generateStateKey();
    const isFirst = this.workflowCopy.States.length === 0;

    const state: WorkflowStateModel = {
      Id: null,
      StateKey: newKey,
      Name: 'New State',
      Description: '',
      IsInitial: isFirst,
      IsFinal: false,
      IsActive: !isFirst
    };

    this.workflowCopy.States.push(state);

    // If we have a parent, create a transition
    if (parentStateKey) {
      const transition: WorkflowTransitionModel = {
        Id: null,
        FromStateKey: parentStateKey,
        ToStateKey: newKey,
        Name: 'Transition',
        Description: '',
        IsManualTrigger: true,
        IsAutomaticTrigger: false,
        MinimumTimeInSourceState: 0,
        Priority: 1
      };
      this.workflowCopy.Transitions.push(transition);
    }

    this.selection = { type: 'state', state };
    this.computeLayout();
    this.emitChange();
  }

  addStateAtDropZone(zone: DropZone, event: MouseEvent): void {
    event.stopPropagation();
    if (zone.isDisabled) return; // Can't add to terminal states
    this.addState(zone.parentStateKey);
  }

  removeSelectedState(): void {
    if (this.readonly || !this.workflowCopy || !this.selectedState) return;

    const stateKey = this.selectedState.StateKey;

    this.workflowCopy.States = this.workflowCopy.States.filter(s => s.StateKey !== stateKey);
    this.workflowCopy.Transitions = this.workflowCopy.Transitions.filter(
      t => t.FromStateKey !== stateKey && t.ToStateKey !== stateKey
    );

    this.selection = { type: null };
    this.computeLayout();
    this.emitChange();
  }

  setStateAsInitial(): void {
    if (this.readonly || !this.workflowCopy || !this.selectedState) return;

    this.workflowCopy.States.forEach(s => s.IsInitial = false);
    this.selectedState.IsInitial = true;
    this.selectedState.IsFinal = false;
    this.selectedState.IsActive = true;
    this.computeLayout();
    this.emitChange();
  }

  setStateAsFinal(): void {
    if (this.readonly || !this.workflowCopy || !this.selectedState) return;

    this.selectedState.IsInitial = false;
    this.selectedState.IsFinal = true;
    this.selectedState.IsActive = false;
    this.computeLayout();
    this.emitChange();
  }

  setStateAsActive(): void {
    if (this.readonly || !this.workflowCopy || !this.selectedState) return;

    this.selectedState.IsInitial = false;
    this.selectedState.IsFinal = false;
    this.selectedState.IsActive = true;
    this.computeLayout();
    this.emitChange();
  }

  removeSelectedTransition(): void {
    if (this.readonly || !this.workflowCopy || !this.selectedTransition) return;

    this.workflowCopy.Transitions = this.workflowCopy.Transitions.filter(
      t => t !== this.selectedTransition
    );
    this.selection = { type: null };
    this.computeLayout();
    this.emitChange();
  }

  // === Condition Management ===

  conditionOperators = CONDITION_OPERATORS;
  
  get conditionProperties(): ConditionPropertyDefinition[] {
    return this.availableProperties.length > 0 
      ? this.availableProperties 
      : COMMON_CONDITION_PROPERTIES;
  }

  addCondition(): void {
    if (this.readonly || !this.selectedTransition) return;
    
    if (!this.selectedTransition.Conditions) {
      this.selectedTransition.Conditions = [];
    }
    
    const newCondition: WorkflowConditionModel = {
      PropertyName: '',
      Operator: '==',
      Value: '',
      ExecutionOrder: this.selectedTransition.Conditions.length + 1
    };
    
    this.selectedTransition.Conditions.push(newCondition);
    this.emitChange();
  }

  removeCondition(index: number): void {
    if (this.readonly || !this.selectedTransition?.Conditions) return;
    
    this.selectedTransition.Conditions.splice(index, 1);
    // Re-order remaining conditions
    this.selectedTransition.Conditions.forEach((c, i) => c.ExecutionOrder = i + 1);
    this.emitChange();
  }

  onConditionChange(): void {
    if (!this.readonly) {
      this.emitChange();
    }
  }

  getPropertyType(propertyName: string): string {
    const prop = this.conditionProperties.find(p => p.value === propertyName);
    return prop?.type || 'string';
  }

  getPropertyLabel(propertyName: string): string {
    const prop = this.conditionProperties.find(p => p.value === propertyName);
    return prop?.label || propertyName;
  }

  getOperatorLabel(operator: string): string {
    const op = this.conditionOperators.find(o => o.value === operator);
    return op?.label || operator;
  }

  onPropertyChange(): void {
    if (!this.readonly) {
      this.computeLayout();
      this.emitChange();
    }
  }

  // === Utilities ===

  private generateStateKey(): string {
    if (!this.workflowCopy) return `state-${Date.now()}`;

    let index = this.workflowCopy.States.length + 1;
    let key = `state-${index}`;
    const existing = new Set(this.workflowCopy.States.map(s => s.StateKey.toLowerCase()));
    while (existing.has(key.toLowerCase())) {
      index++;
      key = `state-${index}`;
    }
    return key;
  }

  private cloneWorkflow(model: WorkflowModel): WorkflowModel {
    return {
      Id: model.Id,
      TargetType: model.TargetType,
      Name: model.Name,
      Description: model.Description,
      IsActive: model.IsActive,
      IsDefault: model.IsDefault,
      States: model.States.map(state => ({ ...state })),
      Transitions: model.Transitions.map(transition => ({ ...transition }))
    };
  }

  private emitChange(): void {
    if (!this.workflowCopy) return;
    const updated = this.cloneWorkflow(this.workflowCopy);
    this.workflowChange.emit(updated);
  }

  private getSvgPoint(event: MouseEvent): { x: number; y: number } {
    if (!this.svgElement?.nativeElement) {
      return { x: 0, y: 0 };
    }

    const svg = this.svgElement.nativeElement;
    const rect = svg.getBoundingClientRect();
    const scaleX = this.viewBox.width / rect.width;
    const scaleY = this.viewBox.height / rect.height;

    return {
      x: this.viewBox.x + (event.clientX - rect.left) * scaleX,
      y: this.viewBox.y + (event.clientY - rect.top) * scaleY
    };
  }

  // === Pan & Zoom ===

  onCanvasMouseDown(event: MouseEvent): void {
    if (event.button === 1 || (event.button === 0 && event.shiftKey)) {
      this.isPanning = true;
      this.panStart = { x: event.clientX, y: event.clientY };
      event.preventDefault();
    }
  }

  onCanvasWheel(event: WheelEvent): void {
    event.preventDefault();
    const zoomFactor = event.deltaY > 0 ? 1.1 : 0.9;
    const newWidth = this.viewBox.width * zoomFactor;
    const newHeight = this.viewBox.height * zoomFactor;

    const rect = this.canvasContainer?.nativeElement.getBoundingClientRect();
    if (rect) {
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      const ratioX = mouseX / rect.width;
      const ratioY = mouseY / rect.height;

      this.viewBox.x += (this.viewBox.width - newWidth) * ratioX;
      this.viewBox.y += (this.viewBox.height - newHeight) * ratioY;
    }

    this.viewBox.width = newWidth;
    this.viewBox.height = newHeight;
  }

  resetView(): void {
    if (this.layout) {
      this.viewBox = { x: 0, y: 0, width: this.layout.width, height: this.layout.height };
    }
  }

  truncateLabel(text: string, maxLength: number = 16): string {
    return text.length > maxLength ? text.substring(0, maxLength - 1) + '…' : text;
  }

  // Template helpers
  isDraggingNode(node: TreeNode): boolean {
    return this.draggedNode !== null && this.draggedNode.state.StateKey === node.state.StateKey;
  }

  getNodeTransform(node: TreeNode): string {
    if (this.isDraggingNode(node)) {
      return `translate(${this.dragPosition.x},${this.dragPosition.y})`;
    }
    return `translate(${node.x},${node.y})`;
  }
}
